/**
 * The bulk invite's review table: one row per candidate, one column per
 * `{token}` the template uses, showing what each ACTUALLY resolves to.
 *
 * This is the answer to "how do I check the variables before I send" — a table
 * rather than N previews, because the values are what differ between candidates
 * and a column of them makes the odd one out visible at a glance. That is
 * exactly the mis-configured interview link this review exists to catch: seven
 * booking links and one blank cell.
 *
 * Rows re-render as the template above is edited (see inviteRender.ts), so the
 * columns follow the tokens the recruiter actually uses.
 *
 * Split out of BulkInviteModal.tsx to keep both files under the 500-line limit.
 */
import { Fragment } from 'react';
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { AlertTriangle, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { COLORS } from './format';
import type { RenderedRow } from './inviteRender';
import { FixInterviewLink } from './FixInterviewLink';
import { isBlockingWarning, warningText } from './inviteWarnings';

export function ReviewTable({
  rows,
  postingId,
  linkByStage,
  included,
  expanded,
  onToggle,
  onExpand,
}: {
  rows: RenderedRow[];
  /** The role — so a link-related warning can route to the link that caused it. */
  postingId: string;
  /**
   * stageId → the interview link governing it, from the stage's TEMPLATE.
   *
   * Which link governs a stage is a stage-level fact, and the per-candidate
   * `linkId` is only populated when a booking URL was actually minted — which is
   * absent in exactly the failure this routing exists for.
   */
  linkByStage: Map<string, string | null>;
  included: Set<string>;
  expanded: string | null;
  onToggle: (opportunityId: string) => void;
  onExpand: (opportunityId: string) => void;
}) {
  // Union of every row's tokens: a batch spanning two stages uses two templates,
  // and a token missing from one of them reads as an empty cell rather than a
  // missing column.
  const tokens: string[] = [];
  for (const r of rows) for (const v of r.variables) if (!tokens.includes(v.token)) tokens.push(v.token);

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200">
      <table className="w-full min-w-[40rem] border-collapse text-left text-xs">
        <thead className="bg-gray-50 text-gray-500">
          <tr>
            <th className="w-8 px-3 py-2" />
            <th className="px-3 py-2 font-semibold">Candidate</th>
            {tokens.map((t) => (
              <th key={t} className="px-3 py-2 font-mono font-semibold">
                {`{${t}}`}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((rendered) => (
            <CandidateRows
              key={rendered.row.opportunityId}
              rendered={rendered}
              postingId={postingId}
              linkId={linkByStage.get(rendered.row.stageId) ?? rendered.row.linkId ?? null}
              tokens={tokens}
              checked={included.has(rendered.row.opportunityId)}
              isOpen={expanded === rendered.row.opportunityId}
              onToggle={() => onToggle(rendered.row.opportunityId)}
              onExpand={() => onExpand(rendered.row.opportunityId)}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CandidateRows({
  rendered,
  postingId,
  linkId,
  tokens,
  checked,
  isOpen,
  onToggle,
  onExpand,
}: {
  rendered: RenderedRow;
  postingId: string;
  linkId: string | null;
  tokens: string[];
  checked: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onExpand: () => void;
}) {
  const { row, variables, warnings, blocked } = rendered;
  const values = new Map(variables.map((v) => [v.token, v]));
  const sendable = !row.skipped;
  return (
    <Fragment>
      <tr className={`border-t border-gray-100 ${blocked ? 'bg-[#FCDFE4]/50' : 'bg-white'}`}>
        <td className="px-3 py-2 align-top">
          <Checkbox
            checked={checked}
            disabled={!sendable}
            onCheckedChange={onToggle}
            aria-label={`Include ${row.name}`}
          />
        </td>
        <td className="px-3 py-2 align-top">
          <button
            type="button"
            onClick={onExpand}
            className="flex items-start gap-1 text-left"
            disabled={!sendable}
          >
            {sendable ? (
              isOpen ? (
                <ChevronDown className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" />
              ) : (
                <ChevronRight className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" />
              )
            ) : (
              <span className="w-3" />
            )}
            <span>
              <span className="font-semibold text-gray-900">{row.name}</span>
              <span className="block text-[11px] text-gray-500">
                {row.candidateEmail || 'no email'} · {row.stageName}
              </span>
            </span>
          </button>
        </td>
        {tokens.map((t) => (
          <ValueCell key={t} token={t} variable={values.get(t)} />
        ))}
      </tr>

      {warnings.length > 0 && (
        <tr className="bg-white">
          <td />
          <td colSpan={tokens.length + 1} className="px-3 pb-2">
            <ul className="space-y-0.5">
              {warnings.map((code) => (
                <li
                  key={code}
                  className="text-[11px]"
                  style={{ color: isBlockingWarning(code) ? COLORS.redText : '#8A6D00' }}
                >
                  {isBlockingWarning(code) ? (
                    <AlertTriangle className="mr-1 inline h-3 w-3" />
                  ) : (
                    <Info className="mr-1 inline h-3 w-3" />
                  )}
                  {warningText(code)}
                </li>
              ))}
            </ul>
            {/*
              Every warning here except "no email in Lever" is fixed on the interview
              link, and the recruiter was previously told WHAT was wrong with no way
              to reach the thing that was wrong. `row.linkId` is null exactly when the
              stage has no link — which is the case that most needs the route, so the
              component falls back to creating one for this role and stage.
            */}
            {warnings.some(isLinkWarning) && (
              <FixInterviewLink
                linkId={linkId}
                postingId={postingId}
                stageId={row.stageId}
                stageName={row.stageName}
                className="mt-1 inline-flex items-center gap-1 text-[11px] text-blue-600 hover:underline"
              />
            )}
          </td>
        </tr>
      )}

      {isOpen && sendable && (
        <tr className="bg-gray-50">
          <td />
          <td colSpan={tokens.length + 1} className="px-3 pb-3">
            <p className="text-[11px] font-semibold text-gray-700">{rendered.subject}</p>
            <pre className="mt-1 max-h-64 overflow-auto whitespace-pre-wrap break-words font-sans text-[11px] leading-relaxed text-gray-600">
              {rendered.body}
            </pre>
          </td>
        </tr>
      )}
    </Fragment>
  );
}

/**
 * One resolved value. The three failure shapes read differently on purpose:
 * a value that is present, one that resolved to nothing, and one nothing
 * resolved at all — the last would be sent as the literal "{token}".
 */
/**
 * Warnings whose fix is on the interview link rather than in the template or in
 * Lever. `no-candidate-email` is deliberately absent: that one is fixed on the
 * candidate's Lever profile, and pointing it at the link would send the
 * recruiter somewhere that cannot help.
 */
function isLinkWarning(code: string): boolean {
  return (
    code === 'no-link' ||
    code === 'no-booking-link' ||
    code === 'no-interviewers' ||
    code.startsWith('unresolved:') ||
    code.startsWith('empty-critical:') ||
    code.startsWith('empty:')
  );
}

function ValueCell({ token, variable }: { token: string; variable?: { value: string; resolved: boolean } }) {
  const bad = !variable || !variable.resolved || !variable.value.trim();
  const good = variable && variable.resolved && variable.value.trim();
  return (
    <td
      className={`max-w-[14rem] break-words px-3 py-2 align-top ${bad ? 'font-semibold' : 'text-gray-700'}`}
      style={bad ? { color: COLORS.redText } : undefined}
      title={variable?.value || undefined}
    >
      {good ? (
        token === 'bookingLink' ? (
          <a href={variable.value} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
            booking link ↗
          </a>
        ) : (
          variable.value
        )
      ) : variable && !variable.resolved ? (
        `{${token}} unfilled`
      ) : (
        'empty'
      )}
    </td>
  );
}
